// Device-side protection for the timed preferences prompt.
//
// Whether the prompt was shown is normally recorded on the server, but that save can fail (offline,
// a server error) and is then rolled back, which would make the prompt eligible again on every page
// and every launch. So the device keeps its own record, written *before* the prompt opens, and the
// prompt only opens if that record can be written and read back. Anything unexpected means
// "do not show": an unusable app is worse than a missed prompt.

const KEY_PREFIX = 'ckbk.onboarding.preferences.shown.';

// Never show the prompt twice within this time, whatever the server says.
export const SHOW_GAP_MS = 24 * 60 * 60 * 1000;

// Never show it more than this many times on one device for one user.
export const MAX_SHOWS_PER_DEVICE = 3;

// Remembered for the lifetime of the page (it survives a re-render or a remount, not a reload).
let shownThisSession = false;

export const resetShownThisSession = () => {
  shownThisSession = false;
};

const defaultStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (error) {
    // Some browsers throw just for touching localStorage (blocked site data).
    return null;
  }
};

const keyFor = userId => `${KEY_PREFIX}${userId}`;

// The times (ms) the prompt was shown, or null when the record cannot be trusted.
const readShown = (userId, storage) => {
  try {
    const raw = storage.getItem(keyFor(userId));
    if (raw === null) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some(time => !Number.isFinite(time))) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

/**
 * Whether this device may show the prompt to this user right now.
 * Not within the gap of the last showing, not past the cap, not twice in one session, and only if
 * the device record is readable.
 */
export const canShowPrompt = ({ userId, now = Date.now(), storage = defaultStorage() } = {}) => {
  if (!(userId > 0) || !storage || shownThisSession) {
    return false;
  }
  const shown = readShown(userId, storage);
  if (shown === null || shown.length >= MAX_SHOWS_PER_DEVICE) {
    return false;
  }
  const last = shown.length > 0 ? Math.max(...shown) : -Infinity;
  // A showing "in the future" (the clock was changed) gives a negative difference: not allowed.
  return now - last >= SHOW_GAP_MS;
};

/**
 * Records that the prompt is about to be shown. Returns false if the record could not be written
 * and read back, in which case the prompt must not be shown.
 */
export const markPromptShown = ({ userId, now = Date.now(), storage = defaultStorage() } = {}) => {
  if (!(userId > 0) || !storage) {
    return false;
  }
  const shown = readShown(userId, storage);
  if (shown === null) {
    return false;
  }
  try {
    const next = JSON.stringify([...shown, now]);
    storage.setItem(keyFor(userId), next);
    if (storage.getItem(keyFor(userId)) !== next) {
      return false;
    }
  } catch (error) {
    return false;
  }
  shownThisSession = true;
  return true;
};

/**
 * Undoes the most recent showing: used when the prompt was pushed aside before the reader could have
 * seen it, so it does not count against them. Returns whether there was one to undo.
 */
export const unmarkPromptShown = ({ userId, storage = defaultStorage() } = {}) => {
  shownThisSession = false;
  if (!(userId > 0) || !storage) {
    return false;
  }
  const shown = readShown(userId, storage);
  if (shown === null || shown.length === 0) {
    return false;
  }
  try {
    storage.setItem(keyFor(userId), JSON.stringify(shown.slice(0, -1)));
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Forgets every showing recorded on this device for this user (test links only), so the schedule on
 * the server is what decides. Returns whether the record could be cleared.
 */
export const clearPromptRecord = ({ userId, storage = defaultStorage() } = {}) => {
  shownThisSession = false;
  if (!(userId > 0) || !storage) {
    return false;
  }
  try {
    storage.removeItem(keyFor(userId));
    return storage.getItem(keyFor(userId)) === null;
  } catch (error) {
    return false;
  }
};

// Logging out clears local storage. The record of when the prompt was shown holds no personal data,
// only times, and losing it would let the prompt come back after every log out and log in, so it is
// kept across that clear.
export const readGuardRecords = (storage = defaultStorage()) => {
  try {
    const records = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (typeof key === 'string' && key.startsWith(KEY_PREFIX)) {
        records.push([key, storage.getItem(key)]);
      }
    }
    return records;
  } catch (error) {
    return [];
  }
};

export const restoreGuardRecords = (records, storage = defaultStorage()) => {
  try {
    (records || []).forEach(([key, value]) => {
      if (value !== null) {
        storage.setItem(key, value);
      }
    });
  } catch (error) {
    // Nothing more can be done; the prompt then refuses to show without a record.
  }
};
